<template>
  <div class="lang-switcher"
       aria-label="Language selector">
    <div class="lang-display"
         @click="toggle"
         :aria-expanded="open ? 'true' : 'false'">
      <img class="flag-icon"
           :src="currentFlagSrc"
           :alt="currentLabel + ' flag'" />
      <span class="lang-text">{{ currentLabel }}</span>
      <span class="caret">▾</span>
    </div>
    <ul v-show="open"
        class="lang-menu"
        role="listbox">
      <li v-for="opt in options"
          :key="opt.value"
          class="lang-option"
          role="option"
          @click="select(opt.value)">
        <img class="flag-icon"
             :src="opt.flag"
             :alt="opt.label + ' flag'" />
        <span>{{ opt.label }}</span>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: 'LanguageSwitcher',
  data() {
    return {
      current: this.$i18n?.locale || 'en',
      supported: ['en', 'zh', 'ru', 'fr', 'es', 'pt', 'de', 'ar', 'ja', 'ko', 'id'],
      open: false,
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1200,
    }
  },
  computed: {
    label() {
      return this.$t('switch_lang') || 'Language';
    },
    // ✅ 判断是否手机端
    isMobile() {
      return this.windowWidth <= 768
    },
    options() {
      return [
        { value: 'zh', label: '中文', flag: 'https://flagcdn.com/w20/cn.png' },
        { value: 'en', label: 'English', flag: 'https://flagcdn.com/w20/us.png' },
        { value: 'ru', label: 'Русский', flag: 'https://flagcdn.com/w20/ru.png' },
        { value: 'fr', label: 'Français', flag: 'https://flagcdn.com/w20/fr.png' },
        { value: 'es', label: 'Español', flag: 'https://flagcdn.com/w20/es.png' },
        { value: 'pt', label: 'Português', flag: 'https://flagcdn.com/w20/pt.png' },
        { value: 'de', label: 'Deutsch', flag: 'https://flagcdn.com/w20/de.png' },
        { value: 'ar', label: 'العربية', flag: 'https://flagcdn.com/w20/sa.png' },
        { value: 'ja', label: '日本語', flag: 'https://flagcdn.com/w20/jp.png' },
        { value: 'ko', label: '한국어', flag: 'https://flagcdn.com/w20/kr.png' },
        { value: 'id', label: 'Bahasa Indonesia', flag: 'https://flagcdn.com/w20/id.png' },
      ]
    },
    currentLabel() {
      const f = this.options.find(o => o.value === this.current)
      return f ? f.label : this.current
    },
    currentFlagSrc() {
      const f = this.options.find(o => o.value === this.current)
      return f ? f.flag : 'https://flagcdn.com/w20/us.png'
    },
  },
  methods: {
    isSupported(loc) {
      return this.supported.includes(loc)
    },
    toggle() {
      this.open = !this.open
    },
    select(val) {
      this.current = val
      this.onChange()
      this.open = false
    },
    onChange() {
      if (!this.isSupported(this.current)) return
      this.$i18n.locale = this.current
      try {
        localStorage.setItem('locale', this.current)
      } catch (e) {
        console.warn('Persist locale failed:', e)
      }
    },
    onDocClick(e) {
      const el = this.$el
      if (!el) return
      const inside = el.contains(e.target)
      if (!inside) this.open = false
    },
    handleResize() {
      this.windowWidth = window.innerWidth
    },
  },
  mounted() {
    let saved = null
    try {
      saved = localStorage.getItem('locale')
    } catch (e) {
      console.warn('Read locale failed:', e)
    }
    if (saved && this.isSupported(saved)) {
      this.current = saved
      this.$i18n.locale = saved
    }
    document.addEventListener('click', this.onDocClick)
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocClick)
    window.removeEventListener('resize', this.handleResize)
  },
}
</script>

<style scoped>
/* ============================================
   基础样式
   ============================================ */
.lang-switcher {
  position: relative;
  display: inline-block;
}

.lang-display {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  color: #333;
  padding: 6px 10px;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s ease;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.lang-display:hover {
  border-color: #4285f4;
}

.lang-display:active {
  transform: scale(0.96);
}

.flag-icon {
  width: 20px;
  height: 14px;
  object-fit: cover;
  border-radius: 2px;
  flex-shrink: 0;
}

.lang-text {
  font-size: 14px;
}

.caret {
  margin-left: 6px;
  color: #666;
  font-size: 12px;
}

/* ===== 下拉菜单 ===== */
.lang-menu {
  position: absolute;
  right: 0;
  z-index: 1200;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
  padding: 6px 0;
  width: 180px;
  margin-top: 6px;
  max-height: 360px;
  overflow-y: auto;
}

.lang-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  cursor: pointer;
  transition: background 0.15s ease;
  -webkit-tap-highlight-color: transparent;
}

.lang-option:hover {
  background: #f5f7fa;
}

.lang-option:active {
  background: #e8ecf1;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* ============================================
   手机端适配 (<= 768px)
   ============================================ */
@media (max-width: 768px) {
  .lang-display {
    padding: 8px 10px;
    gap: 6px;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
  }

  /* ✅ 手机端隐藏语言文字，只显示国旗和下拉箭头 */
  .lang-text {
    display: none;
  }

  .flag-icon {
    width: 22px;
    height: 16px;
  }

  .caret {
    font-size: 10px;
    margin-left: 2px;
  }

  /* ✅ 下拉菜单全屏适配 */
  .lang-menu {
    position: fixed;
    top: auto;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    border-radius: 16px 16px 0 0;
    padding: 8px 0 20px;
    margin-top: 0;
    max-height: 50vh;
    box-shadow: 0 -6px 24px rgba(0, 0, 0, 0.12);
  }

  /* ✅ 增加选项触摸区域 */
  .lang-option {
    padding: 12px 20px;
    gap: 12px;
  }

  .lang-option span {
    font-size: 15px;
  }

  .lang-option .flag-icon {
    width: 28px;
    height: 20px;
  }
}

/* ============================================
   小屏手机适配 (<= 480px)
   ============================================ */
@media (max-width: 480px) {
  .lang-display {
    padding: 6px 8px;
    gap: 4px;
    border-radius: 4px;
  }

  .flag-icon {
    width: 18px;
    height: 12px;
  }

  .caret {
    font-size: 9px;
  }

  .lang-menu {
    padding: 6px 0 16px;
    max-height: 45vh;
  }

  .lang-option {
    padding: 10px 16px;
    gap: 10px;
  }

  .lang-option span {
    font-size: 14px;
  }

  .lang-option .flag-icon {
    width: 24px;
    height: 16px;
  }
}

/* ============================================
   横屏手机适配
   ============================================ */
@media (max-width: 768px) and (orientation: landscape) {
  .lang-menu {
    max-height: 60vh;
    border-radius: 12px 12px 0 0;
  }

  .lang-option {
    padding: 8px 16px;
  }

  .lang-option span {
    font-size: 14px;
  }
}

/* ============================================
   桌面端大屏适配
   ============================================ */
@media (min-width: 769px) {
  .lang-text {
    display: inline; /* 桌面端显示文字 */
  }
}
</style>